import { httpApi } from '@api/http.api.ts';
import type { CommonTableRequest } from '@routes/sidebar/admin-center/___shared___/types.ts';
import type {
    CommonVesselGroupResponseType,
    CommonVesselGroupType,
    UserType,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/roles/___shared___/types.ts';
import type { EmptyResultsDescription, PaginatedResponse } from '@types';

export type DataApproverRequest = CommonTableRequest;

export interface DataApproverUser {
    id: number;
    firstName: string;
    lastName: string;
    rankOrTitleName: string | null;
    userType: UserType;
}

export interface DataApproverAccessResponse {
    id: number;
    modifiedById: number;
    modifiedByTenantAlias: string;
    modifiedBy: string;
    userType: UserType;
    vesselGroups: CommonVesselGroupType[];
    users: DataApproverUser[];
    version: string | null;
}

export interface UpsertDataApproverAccessRequest {
    userType: UserType;
    userIds: number[];
    vesselGroupIds?: number[];
}

export type DataApproverAccessSearchResponse = PaginatedResponse<DataApproverAccessResponse, EmptyResultsDescription>;
export type DataApproverUsersSearchResponse = PaginatedResponse<DataApproverUser, EmptyResultsDescription>;
export type DataApproverVesselGroupsSearchResponse = PaginatedResponse<
    CommonVesselGroupResponseType,
    EmptyResultsDescription
>;

const getBaseUrl = (tenantAlias: string) => `admin-center/tenants/${tenantAlias}/rbac/data-approver-access`;

export const getApiDataApproverAccess = (
    tenantAlias: string,
    payload: DataApproverRequest,
): Promise<DataApproverAccessSearchResponse> =>
    httpApi
        .post<DataApproverAccessSearchResponse>(`${getBaseUrl(tenantAlias)}/search`, payload)
        .then(({ data }) => data);

export const getApiDataApproverUsers = (
    tenantAlias: string,
    payload: DataApproverRequest,
): Promise<DataApproverUsersSearchResponse> =>
    httpApi
        .post<DataApproverUsersSearchResponse>(`${getBaseUrl(tenantAlias)}/users/search`, payload)
        .then(({ data }) => data);

export const getApiDataApproverVesselGroups = (
    tenantAlias: string,
    payload: DataApproverRequest,
): Promise<DataApproverVesselGroupsSearchResponse> =>
    httpApi
        .post<DataApproverVesselGroupsSearchResponse>(`${getBaseUrl(tenantAlias)}/vessel-groups/search`, payload)
        .then(({ data }) => data);

export const createApiDataApproverAccess = (
    tenantAlias: string,
    payload: UpsertDataApproverAccessRequest,
): Promise<DataApproverAccessResponse> =>
    httpApi.post<DataApproverAccessResponse>(getBaseUrl(tenantAlias), payload).then(({ data }) => data);

export const updateApiDataApproverAccess = (
    tenantAlias: string,
    accessId: number,
    payload: UpsertDataApproverAccessRequest,
): Promise<DataApproverAccessResponse> =>
    httpApi.put<DataApproverAccessResponse>(`${getBaseUrl(tenantAlias)}/${accessId}`, payload).then(({ data }) => data);

export const deleteApiDataApproverAccess = (tenantAlias: string, accessId: number): Promise<any> =>
    httpApi.delete<any>(`${getBaseUrl(tenantAlias)}/${accessId}`).then((data) => data);
